import { Theme } from '@mui/material'
import { useMinimalsTheme } from '@tom-ui/ui'
import { MIN_CANVAS_WIDTH_PX } from '../helpers/canvas-width'
import {
  ContainerViewMode,
  CONTAINER_DETAILS_WIDTH,
  YARD_DRAWERS_WIDTH,
} from '../helpers/yard-view.utils'
import { YardViewStore } from '../stores/yard.view-store'

export const CONTAINER_DETAILS_DRAWER_PADDING_PX = 48

// In bay/row (multi-column) mode the drawer expands to at least this fraction
// of the viewport so columns have room to breathe. Single-slot stack mode
// keeps the narrow column-driven width.
export const MULTI_COLUMN_DRAWER_VIEWPORT_FRACTION = 0.5

export const getMaxColumnsByBreakpoint = (theme: Theme, screenWidth: number) => {
  if (screenWidth >= theme.breakpoints.values.xl) return 4
  if (screenWidth >= theme.breakpoints.values.md) return 3
  if (screenWidth >= theme.breakpoints.values.sm) return 2
  return 1
}

// viewportWidth is passed in (not read via useViewportWidth here) so this hook
// doesn't register its own `resize` listener. `Yard.tsx` owns the single
// `useViewportWidth()` call for the whole page and forwards the value down.
//
// isLeftDrawerOpen lets us cap the drawer width so it can coexist with the
// left accumulator/overview drawer on common laptop viewports. Without the
// cap, the bay/row floor (~viewport × 0.5) plus the 400px left drawer would
// leave less than MIN_CANVAS_WIDTH_PX for the canvas, and `useCanvasWidthGuard`
// would immediately force this drawer closed — making the right detail panel
// unusable alongside the accumulator on anything < 1760px wide.
export const useContainerDetailsDrawerWidth = (
  viewStore: YardViewStore,
  viewportWidth: number,
  isLeftDrawerOpen = false,
): number => {
  const theme = useMinimalsTheme()
  const columnCount =
    viewStore.containerDetailsColumns.length > 0 ? viewStore.containerDetailsColumns.length : 3
  const maxByScreen = getMaxColumnsByBreakpoint(theme, viewportWidth)
  const columnsToRender = Math.max(1, Math.min(maxByScreen, columnCount))
  const computed = columnsToRender * CONTAINER_DETAILS_WIDTH + CONTAINER_DETAILS_DRAWER_PADDING_PX

  const baseWidth =
    viewStore.containerViewMode === ContainerViewMode.STACK
      ? computed
      : Math.max(computed, Math.round(viewportWidth * MULTI_COLUMN_DRAWER_VIEWPORT_FRACTION))

  if (!isLeftDrawerOpen) return baseWidth

  // Reserve room for the left drawer and the minimum canvas. If the viewport
  // is too small to even fit a single-column drawer alongside both, let the
  // canvas-width guard handle the auto-collapse fallback.
  const maxAlongsideLeft = viewportWidth - YARD_DRAWERS_WIDTH - MIN_CANVAS_WIDTH_PX
  if (maxAlongsideLeft < CONTAINER_DETAILS_WIDTH + CONTAINER_DETAILS_DRAWER_PADDING_PX) {
    return baseWidth
  }
  return Math.min(baseWidth, maxAlongsideLeft)
}
